import {
  Box,
  FormControl,
  FormGroup,
  FormLabel,
  Typography,
} from '@mui/material';
import * as yup from 'yup';

import AuthModal from 'components/system/AuthModal/AuthModal';
import { LoadingButton } from 'components/system/buttons';
import { FormTextField } from 'components/system/form';
import FormAutocomplete from 'components/system/form/FormAutocomplete';
import SuccessIcon from 'components/system/Icons/SuccessIcon';
import { Control, FieldValues, useForm } from 'react-hook-form';
import useYupValidationResolver from 'utils/useYupValidationResolver';
import { FORM_ERROR_MESSAGE, PHONE_NUMBER_REGEX_NO_CODE } from 'yupHelper';
import { usePhoneVerification } from '../hooks/usePhoneVerification';
import { IPhoneNumber } from '../types';
import { SetupAccountModal } from './common/SetupAccountModal';

const phoneSchemaValidation = yup.object({
  phone: yup
    .string()
    .required(FORM_ERROR_MESSAGE.REQUIRED)
    .matches(PHONE_NUMBER_REGEX_NO_CODE, {
      message: FORM_ERROR_MESSAGE.INVALID_PHONE_NUMBER,
    }),
  countryCallingCode: yup
    .object()
    .required(FORM_ERROR_MESSAGE.REQUIRED)
    .nullable(),
});

export const PhoneVerificationModal = () => {
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<IPhoneNumber>({
    defaultValues: {
      phone: '',
      countryCallingCode: {
        label: 'PT +351',
        value: 'PT-351',
      },
    },
    resolver: useYupValidationResolver(phoneSchemaValidation),
  });
  const { codeOptions, handleBackToLogin, handleFormSubmit, loading } =
    usePhoneVerification();

  return (
    <SetupAccountModal open={true} handleBackToLogin={handleBackToLogin}>
      <AuthModal.Content>
        <AuthModal.Title sx={{ marginBottom: 10 }}>
          Email Verified
          <SuccessIcon
            sx={{ marginLeft: 4, width: '16px', height: '16px' }}
            titleAccess="success"
          />
        </AuthModal.Title>
        <AuthModal.Title sx={{ marginBottom: 3 }}>
          Add Phone Number
        </AuthModal.Title>
        <Typography variant="textXl" sx={{ marginBottom: 12 }} paragraph>
          Please enter your phone number to set up two-factor authentication.
        </Typography>
        <FormGroup>
          <FormControl>
            <FormLabel required htmlFor="phone">
              Phone number
            </FormLabel>
            <Box sx={{ display: 'flex', gap: 3 }}>
              <FormAutocomplete
                control={control as unknown as Control<FieldValues>}
                id="countryCallingCode"
                name="countryCallingCode"
                options={codeOptions}
                required
                variant="standard"
                error={errors.countryCallingCode?.message}
                sx={{ width: '125px' }}
              />
              <FormTextField
                {...register('phone')}
                id="phone"
                variant="standard"
                required
                error={errors.phone?.message}
                placeholder="Phone number"
              />
            </Box>
          </FormControl>
        </FormGroup>
      </AuthModal.Content>
      <AuthModal.Buttons>
        <LoadingButton
          onClick={handleSubmit(handleFormSubmit)}
          loading={loading}
        >
          Submit
        </LoadingButton>
      </AuthModal.Buttons>
    </SetupAccountModal>
  );
};
